import {
  Banner,
  Flex,
  Link,
  Skeleton,
  Spacer,
  spaces,
  Text,
  sizes,
} from '@electricjs/arc';
import { OSType } from '@electricjs/core_entity-client';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import { NoWidowText } from '@/components/Assets/AssetDetails/common';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import {
  encryptionLinks,
  firewallLinks,
  gatekeeperLinks,
  osLinks,
  storageLinks,
} from '@/constants/deviceIssueLinks';
import { AssetDeviceProperty, AssetIssue } from '@/types/assets';
import { DeviceIssueType } from '@/types/devices';
import { Employee } from '@/types/employees';
import { getDisplayFullName } from 'common/utils/getFullName';
import { getLocalizedAppleUrl } from '@/helpers/getLocalizedAppleUrl';
import { useGetLatestOsVersions } from '@/hooks/useGetLatestOsVersions';
import EmailModal from './EmailModal';

type DeviceIssueData = {
  [key in keyof AssetIssue]: {
    titleIssueType: string;
    descriptionAction: string;
    windowsInstructionsLabel: string | null;
    macInstructionsLabel: string;
    windowsLink: string | null;
    macLink: string;
    latestMacUpdate?: string;
    latestWindowsUpdate?: string;
  };
};

const getDeviceIssueModalData = (t: TFunction): DeviceIssueData => ({
  [DeviceIssueType.OS]: {
    titleIssueType: t('deviceSecurityPanel.issueModals.osUpdate.title'),
    descriptionAction: t(
      'deviceSecurityPanel.issueModals.osUpdate.description'
    ),
    windowsInstructionsLabel: t(
      'deviceSecurityPanel.issueModals.osUpdate.windowsInstructions'
    ),
    macInstructionsLabel: t(
      'deviceSecurityPanel.issueModals.osUpdate.macInstructions'
    ),
    windowsLink: osLinks.windows,
    macLink: getLocalizedAppleUrl(osLinks.mac),
  },
  [DeviceIssueType.Encryption]: {
    titleIssueType: t('deviceSecurityPanel.issueModals.encryption.title'),
    descriptionAction: t(
      'deviceSecurityPanel.issueModals.encryption.description'
    ),
    windowsInstructionsLabel: t(
      'deviceSecurityPanel.issueModals.encryption.windowsInstructions'
    ),
    macInstructionsLabel: t(
      'deviceSecurityPanel.issueModals.encryption.macInstructions'
    ),
    windowsLink: encryptionLinks.windows,
    macLink: getLocalizedAppleUrl(encryptionLinks.mac),
  },
  [DeviceIssueType.Firewall]: {
    titleIssueType: t('deviceSecurityPanel.issueModals.firewall.title'),
    descriptionAction: t(
      'deviceSecurityPanel.issueModals.firewall.description'
    ),
    windowsInstructionsLabel: t(
      'deviceSecurityPanel.issueModals.firewall.windowsInstructions'
    ),
    macInstructionsLabel: t(
      'deviceSecurityPanel.issueModals.firewall.macInstructions'
    ),
    windowsLink: firewallLinks.windows,
    macLink: getLocalizedAppleUrl(firewallLinks.mac),
  },
  [DeviceIssueType.Gatekeeper]: {
    titleIssueType: t('deviceSecurityPanel.issueModals.gatekeeper.title'),
    descriptionAction: t(
      'deviceSecurityPanel.issueModals.gatekeeper.description'
    ),
    windowsInstructionsLabel: null,
    macInstructionsLabel: t(
      'deviceSecurityPanel.issueModals.gatekeeper.macInstructions'
    ),
    windowsLink: null,
    macLink: getLocalizedAppleUrl(gatekeeperLinks.mac),
  },
  [DeviceIssueType.Storage]: {
    titleIssueType: t('deviceSecurityPanel.issueModals.storage.title'),
    descriptionAction: t('deviceSecurityPanel.issueModals.storage.description'),
    windowsInstructionsLabel: t(
      'deviceSecurityPanel.issueModals.storage.windowsInstructions'
    ),
    macInstructionsLabel: t(
      'deviceSecurityPanel.issueModals.storage.macInstructions'
    ),
    windowsLink: storageLinks.windows,
    macLink: getLocalizedAppleUrl(storageLinks.mac),
  },
});

type IssueModalProps = {
  issueType: keyof AssetIssue;
  employee: Employee;
  device: AssetDeviceProperty;
};

const IssueModal = ({ issueType, employee, device }: IssueModalProps) => {
  const { t } = useTranslation('assets');
  const { closeModal } = useGlobalUI();

  // Fetch EOL data to extract latest OS versions
  const {
    latest_macos_version: latestMacUpdate,
    latest_windows_version: latestWindowsUpdate,
    isError,
    isLoading,
  } = useGetLatestOsVersions();

  const issueModalData = getDeviceIssueModalData(t)[issueType];

  if (!issueModalData) {
    return null;
  }

  const employeeFullname = getDisplayFullName(employee, 'the employee');

  return (
    <EmailModal
      hide={closeModal}
      issueType={issueType}
      employeeId={employee.id}
      employeeFullname={employeeFullname}
      employeeEmail={employee.email}
      device={device}
      title={t('deviceSecurityPanel.issueModals.modalTitle', {
        issueType: issueModalData.titleIssueType,
      })}>
      <NoWidowText>
        {t('deviceSecurityPanel.issueModals.modalBody', {
          employeeName: employeeFullname,
          action: issueModalData.descriptionAction,
        })}
      </NoWidowText>
      <>
        {device.osType !== OSType.Darwin &&
          issueModalData.windowsLink &&
          issueModalData.windowsInstructionsLabel && (
            <>
              <Spacer y={3} />
              <Text>
                <Link
                  target="_blank"
                  rel="noopener"
                  href={issueModalData.windowsLink}>
                  {issueModalData.windowsInstructionsLabel}
                </Link>
              </Text>
              {issueType === DeviceIssueType.OS &&
                !isError &&
                (isLoading || latestWindowsUpdate) && (
                  <>
                    <Spacer y={3} />
                    <Flex gap={spaces.space_xs}>
                      <Text tag="span" fontWeight="bold">
                        {t(
                          'deviceSecurityPanel.issueModals.latestWindowsUpdate'
                        )}
                      </Text>
                      {isLoading ? (
                        <Skeleton width="100px" height={sizes.size_16} />
                      ) : (
                        <Text>{latestWindowsUpdate}</Text>
                      )}
                    </Flex>
                  </>
                )}
            </>
          )}
        {device.osType !== OSType.Windows && (
          <>
            <Spacer y={3} />
            <Text>
              <Link
                target="_blank"
                rel="noopener"
                href={issueModalData.macLink}>
                {issueModalData.macInstructionsLabel}
              </Link>
            </Text>
            {issueType === DeviceIssueType.OS &&
              !isError &&
              (isLoading || latestMacUpdate) && (
                <>
                  <Spacer y={3} />
                  <Flex gap={spaces.space_xs}>
                    <Text tag="span" fontWeight="bold">
                      {t('deviceSecurityPanel.issueModals.latestMacUpdate')}
                    </Text>
                    {isLoading ? (
                      <Skeleton width="100px" height={sizes.size_16} />
                    ) : (
                      <Text>{latestMacUpdate}</Text>
                    )}
                  </Flex>
                </>
              )}
          </>
        )}
        {employee.email === null && (
          <Flex pt="1rem">
            <Banner
              id="employee-no-work-email-banner"
              isFlat
              isFullWidth
              intent="error"
              title={t('deviceSecurityPanel.issueModals.emailCannotBeSent')}
              message={t('deviceSecurityPanel.issueModals.noWorkEmail')}
            />
          </Flex>
        )}
      </>
    </EmailModal>
  );
};

export default IssueModal;
